import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TableColumns, Search } from "../../../configs/accountingAccount";
import { AccountingAccountRow } from "./AccountingAccountTable";
import { AccountingAccount } from "../../../data/entities";
import { api } from "../../../shared/hooks/api";
import {
    ContentWrapper,
    ExpandableContent,
} from "../../../shared/styled/TableStyles";
import Spinner from "../../../shared/components/LoadingPage";
import CustomTable from "../../../shared/components/CustomTable";
import SearchFilterBar from "../../../shared/components/SearchFilterBar";
import PaginationBar from "../../../shared/components/PaginationBar";
import { DEFAULT_PAGE_SIZE } from "../../../configs/apiConfig";

/**
 * Simple Parent-Child Structure for Accounting Accounts
 *
 * This component displays accounting accounts with simple parent-child relationships:
 * - Parent accounts (accounts with no parent property) are shown first
 * - Child accounts are initially hidden under their parent
 * - Clicking the + button expands to show children, - button collapses to hide them
 * - Child accounts are indented by 24px to show the relationship
 * - Only 1 level of hierarchy: Parent -> Children (no nested children)
 */

interface DisplayAccount extends AccountingAccount {
    level: number; // 0 for root, 1+ for descendants
}

const Content = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Initialize state from URL parameters
    const getInitialStateFromURL = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const urlPageSize = params.get('page_size');
        const urlPage = params.get('page');
        const urlSearch = params.get('search');
        
        return {
            pageSize: urlPageSize ? parseInt(urlPageSize, 10) : DEFAULT_PAGE_SIZE,
            currentPage: urlPage ? parseInt(urlPage, 10) : 1,
            search: urlSearch || ''
        };
    }, [location.search]);

    const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
    const [displayAccounts, setDisplayAccounts] = useState<DisplayAccount[]>([]);
    const [currentPage, setCurrentPage] = useState(getInitialStateFromURL().currentPage);
    const [pageSize, setPageSize] = useState(getInitialStateFromURL().pageSize);
    const [search, setSearch] = useState(getInitialStateFromURL().search);

    // API call
    const [{ data, isLoading }, originalFetchItems] = api.get(
        "/accounting/accounts/",
        {
            page_size: pageSize,
            page: currentPage,
            search: search
        },
        false
    );

    // Wrapper function to handle search parameters from SearchBar
    const fetchItems = useCallback((searchParams?: any) => {
        if (searchParams && searchParams.search !== undefined) {
            setSearch(searchParams.search);
            setCurrentPage(1); // Reset to page 1 when searching
            updateURLParams(pageSize, 1, searchParams.search);
        }
        originalFetchItems(searchParams);
    }, [originalFetchItems, pageSize]);

    // Update URL when pagination or search changes
    const updateURLParams = useCallback((newPageSize?: number, newPage?: number, newSearch?: string) => {
        const params = new URLSearchParams();
        
        if (newPageSize !== undefined) {
            params.set('page_size', newPageSize.toString());
        }
        if (newPage !== undefined) {
            params.set('page', newPage.toString());
        }
        if (newSearch !== undefined) {
            if (newSearch.trim() !== '') {
                params.set('search', newSearch);
            }
        } else if (search) {
            params.set('search', search);
        }
        
        const newURL = `${location.pathname}?${params.toString()}`;
        navigate(newURL, { replace: true });
    }, [location.pathname, search, navigate]);

    // Handle page size change
    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to page 1 when changing page size
        updateURLParams(newPageSize, 1);
    }, [updateURLParams]);

    // Handle page change
    const handlePageChange = useCallback((newPage: number) => {
        setCurrentPage(newPage);
        updateURLParams(undefined, newPage);
    }, [updateURLParams]);

    // Toggle expanded state for parent accounts
    const toggleExpanded = useCallback((parentId: string) => {
        setExpandedParents((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(parentId)) {
                newSet.delete(parentId);
            } else {
                newSet.add(parentId);
            }
            return newSet;
        });
    }, []);

    // Listen for URL changes and update state
    useEffect(() => {
        const { pageSize: urlPageSize, currentPage: urlPage, search: urlSearch } = getInitialStateFromURL();
        
        if (urlPageSize !== pageSize) {
            setPageSize(urlPageSize);
        }
        if (urlPage !== currentPage) {
            setCurrentPage(urlPage);
        }
        if (urlSearch !== search) {
            setSearch(urlSearch);
        }
    }, [location.search, pageSize, currentPage, search, getInitialStateFromURL]);

    // Fetch data when pagination or search changes
    useEffect(() => {
        originalFetchItems();
    }, [pageSize, currentPage, search]);

    // Process data and build display accounts
    useEffect(() => {
        if (data?.results) {
            const accounts = data.results as AccountingAccount[];

            // Build index of children by parentId for efficient traversal
            const childrenByParent = new Map<string | null, AccountingAccount[]>();
            accounts.forEach((acc) => {
                const key = (acc.parent as string) || null;
                const arr = childrenByParent.get(key) || [];
                arr.push(acc);
                childrenByParent.set(key, arr);
            });

            const result: DisplayAccount[] = [];

            const pushNodeAndDescendants = (node: AccountingAccount, level: number) => {
                result.push({ ...node, level });
                if (!expandedParents.has(node.id)) return;
                const children = childrenByParent.get(node.id) || [];
                // Optional: sort children by code for stable ordering
                children
                    .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
                    .forEach((child) => pushNodeAndDescendants(child, level + 1));
            };

            // Start from roots (parent null)
            const roots = (childrenByParent.get(null) || [])
                .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
            roots.forEach((root) => pushNodeAndDescendants(root, 0));

            setDisplayAccounts(result);
        }
    }, [data, expandedParents]);

    const totalItems = data?.count || 0;

    return (
        <ContentWrapper>
            <ExpandableContent>
                <SearchFilterBar
                    textString={Search.searchText}
                    fetchItems={fetchItems}
                    setCurrentPage={setCurrentPage}
                />
                {isLoading ? (
                    <Spinner />
                ) : (
                    <CustomTable
                        itemList={displayAccounts}
                        RowElement={AccountingAccountRow}
                        TableColumns={TableColumns}
                        extraRowProps={{
                            fetchItems,
                            expandedParents,
                            toggleExpanded,
                            allAccounts: data?.results || [],
                            onRefresh: fetchItems,
                        }}
                    />
                )}
            </ExpandableContent>
            <PaginationBar
                totalItems={totalItems}
                pageSize={pageSize}
                setPageSize={handlePageSizeChange}
                fetchItems={fetchItems}
                currentPage={currentPage}
                setCurrentPage={handlePageChange}
            />
        </ContentWrapper>
    );
};

export default Content;
